import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const GOALS = [
  {
    id: 'maximize-uptime',
    title: 'Maximize Uptime',
    description: 'Prioritize keeping equipment running with minimal downtime',
    icon: <SettingsIcon sx={{ fontSize: 28, color: '#6B7280' }} />,
  },
  {
    id: 'minimize-cost',
    title: 'Minimize Cost',
    description: 'Prioritize cost-effective maintenance and operations',
    icon: <AttachMoneyIcon sx={{ fontSize: 28, color: '#6B7280' }} />,
  },
];

const INTENSITY_OPTIONS = [
  {
    id: 'conservative',
    title: 'Conservative',
    description: 'Cautious approach. Act only when high confidence signals appear.',
    bullets: [
      'Near-reactive maintenance only when urgently needed',
      'Avoid taking preventive actions without strong evidence',
      'Minimize unnecessary work orders',
    ],
  },
  {
    id: 'balanced',
    title: 'Balanced',
    description: 'Smart middle ground with proactive scheduling and timing',
    bullets: [
      'Schedule maintenance with buffer time before failures',
      'Balance cost efficiency with uptime protection',
      'Batch related work to optimize efficiency',
    ],
  },
  {
    id: 'aggressive',
    title: 'Aggressive',
    description: 'Proactive stance. Act early on emerging signals.',
    bullets: [
      'Preemptive actions even with partial confidence signals',
      'Schedule maintenance well ahead of predicted failures',
      'Prioritize uptime over cost considerations',
    ],
  },
];

const GoalCard = ({ goal, isSelected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 2,
      border: '1.5px solid',
      borderColor: isSelected ? '#399432' : '#E5E7EB',
      borderRadius: 3,
      cursor: 'pointer',
      bgcolor: isSelected ? '#F0FDF4' : '#fff',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: isSelected ? '#399432' : '#D1D5DB',
        bgcolor: isSelected ? '#F0FDF4' : '#F9FAFB',
      },
    }}
  >
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#1B2A3D' }}>
        {goal.title}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: '#6B7280', mt: 0.25 }}>
        {goal.description}
      </Typography>
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        flexShrink: 0,
        ml: 2,
      }}
    >
      {goal.icon}
    </Box>
  </Box>
);

const IntensityCard = ({ option, isSelected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2.5,
      border: '1.5px solid',
      borderColor: isSelected ? '#399432' : '#E5E7EB',
      borderRadius: 3,
      cursor: 'pointer',
      bgcolor: isSelected ? '#F0FDF4' : '#fff',
      position: 'relative',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: isSelected ? '#399432' : '#D1D5DB',
        bgcolor: isSelected ? '#F0FDF4' : '#F9FAFB',
      },
    }}
  >
    {isSelected && (
      <CheckCircleIcon
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          fontSize: 24,
          color: '#399432',
        }}
      />
    )}
    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1B2A3D', mb: 0.5 }}>
      {option.title}
    </Typography>
    <Typography sx={{ fontSize: '0.8125rem', color: '#6B7280', mb: 1.5 }}>
      {option.description}
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {option.bullets.map((bullet, idx) => (
        <Box
          component="li"
          key={idx}
          sx={{
            fontSize: '0.8125rem',
            color: '#4B5563',
            lineHeight: 1.7,
          }}
        >
          {bullet}
        </Box>
      ))}
    </Box>
  </Box>
);

const OptimizationGoalsModal = ({ open, onClose, onSave }) => {
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedIntensity, setSelectedIntensity] = useState(null);
  const [step, setStep] = useState('goals'); // 'goals' | 'intensity'

  const handleGoalSelect = (goalId) => {
    setSelectedGoal(goalId);
    setSelectedIntensity(null);
    setStep('intensity');
  };

  const handleBack = () => {
    setStep('goals');
    setSelectedIntensity(null);
  };

  const handleCancel = () => {
    setSelectedGoal(null);
    setSelectedIntensity(null);
    setStep('goals');
    onClose();
  };

  const handleSave = () => {
    if (onSave) {
      onSave({ goal: selectedGoal, intensity: selectedIntensity });
    }
    handleCancel();
  };

  const selectedGoalData = GOALS.find((g) => g.id === selectedGoal);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.15)',
          maxWidth: 520,
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        {step === 'goals' ? (
          <>
            {/* Header */}
            <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: '#1B2A3D', mb: 0.5 }}>
              Optimization Goals
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#399432', mb: 3 }}>
              Choose what to optimize for
            </Typography>

            {/* Goal Options */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {GOALS.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  isSelected={selectedGoal === goal.id}
                  onClick={() => handleGoalSelect(goal.id)}
                />
              ))}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                sx={{
                  color: '#374151',
                  borderColor: '#D1D5DB',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  px: 2.5,
                  '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={!selectedGoal}
                startIcon={<SaveIcon sx={{ fontSize: '18px !important' }} />}
                sx={{
                  bgcolor: '#399432',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  px: 2.5,
                  '&:hover': { bgcolor: '#2E7628' },
                  '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                }}
              >
                Save Settings
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Breadcrumb Header */}
            <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mb: 0.5 }}>
              Optimization Goals /{' '}
              <Box component="span" sx={{ color: '#6B7280' }}>
                {selectedGoalData?.title}
              </Box>
            </Typography>

            <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: '#1B2A3D', mb: 0.5 }}>
              {selectedGoalData?.title}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#399432', mb: 3 }}>
              Choose how strongly to optimize
            </Typography>

            {/* Intensity Options */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {INTENSITY_OPTIONS.map((option) => (
                <IntensityCard
                  key={option.id}
                  option={option}
                  isSelected={selectedIntensity === option.id}
                  onClick={() => setSelectedIntensity(option.id)}
                />
              ))}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button
                onClick={handleBack}
                variant="outlined"
                sx={{
                  color: '#374151',
                  borderColor: '#D1D5DB',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  px: 2.5,
                  mr: 'auto',
                  '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleCancel}
                variant="outlined"
                sx={{
                  color: '#374151',
                  borderColor: '#D1D5DB',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  px: 2.5,
                  '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={!selectedIntensity}
                startIcon={<SaveIcon sx={{ fontSize: '18px !important' }} />}
                sx={{
                  bgcolor: '#399432',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  px: 2.5,
                  '&:hover': { bgcolor: '#2E7628' },
                  '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                }}
              >
                Save Settings
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OptimizationGoalsModal;
